import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import DownloadInstagramContent from './DownloadInstagramContent';
import ChooseContent from './ChooseContent';
import WhyDownloadStoriesContent from './WhyDownloadStoriesContent';
import FAQContent from './FAQContent';
import BestTipsContent from '../LandingPage/BestTipsContent';
import DownloadStoriesBottomContent from '../PostComments/DownloadStoriesBottomContent';
import InstagramUserInfoSkeleton from './InstagramUserInfoSkeleton';
import CommentsPlan from './CommentsPlan';
import {
  fetchCreateTaskApi,
  fetchGetExportNumber,
  fetchGetTaskListApi,
  fetchOrderSubsListRequest,
} from '@/lib/service/home';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import { message } from 'antd';
import { fetchInsMonitorSearch } from '@/lib/service';
import { InsAccountModel } from '@/types/home';
import { isInsUserUrl } from '@/lib/utils';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import useFollowExportPremium from '@/lib/hooks/isPremium/useFollowExportPremium';
import { useRouter } from 'next/router';
import Searching from './Searching';
import DataPanel from './DataPanel';
import InstagramUserInfo from './InstagramUserInfo';

const AlterModal = dynamic(() => import('./AlterModal'), { ssr: false });
const AlterModalLimit = dynamic(() => import('./AlterModalLimit'), { ssr: false });

export interface ITaskListData {
  task: Task;
  pages: Pages;
}

export interface Task {
  id: number;
  uid: string;
  target_media_name: string;
  export_type: string;
  is_paid_export: boolean;
  export_count: number;
  exported_users: any;
  remark: any;
  media_name: string;
  full_name: string;
  profile_pic_url: string;
  private_mark: any;
  verified: boolean;
  media_url: string;
  current_export_count: number;
  status: number;
  insert_time: string;
  follower_count: number;
  following_count: number;
  media_count: number;
  csv_url: string;
  excel_url: string;
}

export interface Pages {
  message: string;
  code: number;
  total: number;
  data: Daum[];
  has_next: boolean;
}

export interface Daum {
  id: string;
  task_id: number;
  pk_id: string;
  profile_pic_url: string;
  username: string;
  full_name: string;
  verified: boolean;
}

const StartTastModal = dynamic(() => import('./StartTastModal'), { ssr: false });

const TopSearchContent = dynamic(() => import('./TopSearch'), { ssr: true });
const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const UserDashboard = dynamic(() => import('../Dashboard/UserDashboard'), { ssr: false });

export default function IgFollowerExport({ data }: any) {
  const contentData = data?.fetchDataRes;
  const { t } = useTranslation('common');
  const [searchStr, setSearchStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUserPlan, setCurrentUserPlan] = useState<any>(null);
  const [isPlanReady, setIsPlanReady] = useState(false);
  const { isLogin, guideToRegisterOrLogin } = useRegisterOrLoginGuide();
  const [currentInsUser, setCurrentInsUser] = useState<InsAccountModel>(null);
  const currentUserRef = useRef<InsAccountModel>(null);
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const [openStartTastModal, setOpenStartTastModal] = useState<boolean>(false);
  const { isPremium } = useFollowExportPremium();
  // CLS 优化：记录 TopSearch 区域的 ref 和首次滚动位置
  const topSearchRef = useRef<HTMLDivElement>(null);
  const firstScrollPositionRef = useRef<number | null>(null);

  // 计算滚动位置：让标题对齐视口顶部（减去 header 高度）
  const getScrollToTitlePosition = () => {
    // 查找标题元素（class 包含 title）
    const titleElement = topSearchRef.current?.querySelector('[class*="title"]') as HTMLElement;
    if (titleElement) {
      // 获取 header 高度
      const headerContainer = document.querySelector('header');
      const headerHeight = headerContainer?.offsetHeight || 64;
      // 计算标题相对于页面的位置，减去 header 高度
      const titleRect = titleElement.getBoundingClientRect();
      const currentScrollTop = document.scrollingElement?.scrollTop || 0;
      return titleRect.top + currentScrollTop - headerHeight;
    }
    // fallback：使用 topSearchRef 的位置
    return topSearchRef.current?.offsetTop || 0;
  };

  const onValueChanged = (e?: string) => {
    setSearchStr(e);
  };

  const router = useRouter();

  const [type, setType] = useState<'following' | 'followers'>('followers');
  const originalSearchStrRef = useRef<string>('');

  useEffect(() => {
    if (!router.isReady || !isPlanReady) return;
    const { username, relation, type, title } = router.query;
    if (username && typeof username === 'string') {
      setSearchStr(decodeURIComponent(username));
    }
    if (type && typeof type === 'string') {
      setType(type as 'following' | 'followers');
    }
    if (relation && (relation === 'followers' || relation === 'following')) {
      setType(relation);
    }
    if (title) {
      const PricingTitle = document.querySelector(`#${title}`);
      if (PricingTitle) {
        PricingTitle.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // 只有这两个值并存的时候才说明是从dashboard跳转过来的
    if (username && type) {
      onSearch(decodeURIComponent(username as string));
    }
  }, [router.isReady, router.query, isPlanReady]);

  const hasValidSubscription = useMemo(() => {
    return currentUserPlan?.expire_time ? new Date(currentUserPlan.expire_time) >= new Date() : false;
  }, [currentUserPlan?.expire_time]);

  const onSearch = async (e?: string) => {
    if (!e) return;
    if (!isLogin) {
      guideToRegisterOrLogin(() => {
        console.log(e);
      });
      return;
    }
    if (isSearchLoading || loading) {
      return;
    }
    setLoading(true);
    const res = await fetchInsMonitorSearch(e, undefined, { bizFuncName: 'follow_export', isPremium });
    if (res.code == 0) {
      const { follower_count, following_count } = res?.data[0];
      if ((type == 'followers' && follower_count == 0) || (type == 'following' && following_count == 0)) {
        setLoading(false);
        message.warning(t('This account has no follower or following data to export.'));
        return;
      }
      currentUserRef.current = res.data[0];
      const data = {
        is_private: res?.data[0]?.is_private,
        media_name: res?.data[0]?.media_name,
        profile_pic_url: res?.data[0]?.profile_pic_url,
      };
      if (data?.is_private == 1) {
        setLoading(false);
        setCurrentInsUser(res.data[0]);
        setOpenPrivateAccount({
          title: t('OopsPrivateProfileDetected'),
          desc: (
            <>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>{t('ThisInstagramAccountIsPrivate_Desc')}</div>
              <UserDashboard {...data} />
            </>
          ),
          btnTitle: t('TRYAGAIN'),
          btnDesc: (
            <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
              {t('Note: Only public accounts can be tracked.')}
            </p>
          ),
        });
      } else if (data?.is_private == 2) {
        setLoading(false);
        setOpenPrivateAccount({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
        });
      } else {
        if (!hasValidSubscription) {
          // CLS 优化：弹出 Warning 弹窗时保持 loading 状态（骨架屏），不设置 currentInsUser
          // 等用户点击 No thanks 后再切换状态
          handleOpenErrorAlterModal(res.data[0]);
          return;
        }
        setLoading(false);
        setCurrentInsUser(res.data[0]);
        // createTask(5000);
        setOpenStartTastModal(true);
      }
    } else {
      setLoading(false);
    }
  };

  const [taskListData, setTaskListData] = useState<ITaskListData>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showPeal, setShowPeal] = useState(false);
  const [openErrorAlterModal, setOpenErrorAlterModal] = useState<any>(null);
  const [openErrorAlterModalLimit, setOpenErrorAlterModalLimit] = useState<any>(null);
  const handleOpenErrorAlterModal = (data?: InsAccountModel) => {
    setOpenErrorAlterModal({
      title: t('Warning'),
      desc: t(
        `Your free plan limits you to exporting only 500 xxx1 at a time, This account has xxx2 xxx3. Would you like to upgrade your plan to export more data?`
      )
        .replace('xxx1', type == 'followers' ? 'followers' : 'following')
        .replace(
          'xxx2',
          type == 'followers' ? data?.follower_count.toLocaleString() : data?.following_count.toLocaleString()
        )
        .replace('xxx3', type == 'followers' ? 'followers' : 'following'),
      btnTitle: t('Upgrade Plan'),
      type: true,
      cancelBtnTitle: t('No, thanks'),
    });
  };

  const createTask = async (exportCount: number) => {
    const res = await fetchCreateTaskApi({
      target_media_name: originalSearchStrRef.current,
      media_name: currentInsUser?.media_name || currentUserRef.current?.media_name,
      full_name: currentInsUser?.full_name || currentUserRef.current?.full_name,
      profile_pic_url: currentInsUser?.profile_pic_url || currentUserRef.current?.profile_pic_url,
      verified: currentInsUser?.is_verified || currentUserRef.current?.is_verified,
      media_url: currentInsUser?.media_url,
      export_type: type,
      export_count: exportCount,
      follower_count: currentInsUser?.follower_count || currentUserRef.current?.follower_count,
      following_count: currentInsUser?.following_count || currentUserRef.current?.following_count,
      media_count: currentInsUser?.media_count || currentUserRef.current?.media_count,
    });
    if (res.code == 0) {
      implamentFunc(res.data.id);
      setShowPeal(true);
      setIsSearchLoading(true);
    } else {
      // CLS 优化：不在这里重置 currentInsUser，等弹窗关闭后再重置，保持进度条显示
      if (res.code == 23005) {
        if (hasValidSubscription) {
          //中级
          if (currentUserPlan?.name == 'Pro Monthly' || currentUserPlan?.name == 'Pro Yearly') {
            setOpenErrorAlterModalLimit({
              title: t("You've maxed out your Pro plan"),
              desc: t('Move up to Business to export data from bigger accounts and unlock deeper follower insights.'),
              btnTitle: t('Upgrade to Business'),
            });
          }
          //最高级
          if (currentUserPlan?.name == 'Business Monthly' || currentUserPlan?.name == 'Business Yearly') {
            setOpenErrorAlterModalLimit({
              title: t("You've used all your exports for this month"),
              desc: t('Your export limit will refresh automatically next month. Thanks for growing with us.'),
              btnTitle: t('Got it'),
            });
          }
        } else {
          setOpenErrorAlterModalLimit({
            title: t("You've hit your free limit"),
            desc: t('Upgrade to unlock full exports and pull follower or following data from any size account.'),
            btnTitle: t('Upgrade'),
          });
        }
      }
    }
  };

  const implamentFunc = async (taskId: string) => {
    let isWhile = true;
    while (isWhile) {
      const res = await fetchGetTaskListApi(taskId, 1, 10);
      if (res.code == 0) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        setTaskListData({ ...res.data });
        if (res.data.task.status === 0) {
        } else {
          isWhile = false;
        }
      }
    }
  };

  const numberRef = useRef<number>(0);
  const [exportNumber, setExportNumber] = useState<number>(0);
  const refreshNumber = async () => {
    const res = await fetchGetExportNumber();
    if (res.code == 0) {
      numberRef.current = res.data;
      setExportNumber(res.data);
    }
  };

  const rightStr = useRef<string>('');

  useEffect(() => {
    if (isLogin) {
      setIsPlanReady(false);
      fetchOrderSubsListRequest('follow_export', data.locale)
        .then((res: any) => {
          setCurrentUserPlan(res.data?.length > 0 ? res.data[res.data.length - 1] : null);
        })
        .catch(() => {
          setCurrentUserPlan(null);
        })
        .finally(() => {
          setIsPlanReady(true);
        });
      refreshNumber();
    } else {
      setCurrentUserPlan(null);
      setIsPlanReady(true);
    }
  }, [isLogin]);

  return (
    <div>
      <div className="max-w-[1200px] mx-auto px-2 md:px-0 mb-10">
        <div ref={topSearchRef}>
          <TopSearchContent
            topInfo={contentData?.contentInfo?.hero}
            loading={loading}
            searchVal={searchStr}
            currentInsUser={currentInsUser}
            onSearchClick={(e) => {
              originalSearchStrRef.current = e;
              // CLS 优化：记录首次滚动位置（标题对齐视口顶部）
              const targetScrollTop = getScrollToTitlePosition();
              firstScrollPositionRef.current = targetScrollTop;
              if (!e) return;
              const searchStr = e.trim();
              if (searchStr.includes('/reel/') || searchStr.includes('/reels/')) {
                setOpenPrivateAccount({
                  title: t('Invalid Instagram Username'),
                  desc: t('The username you entered is not valid. Please check the username and try again.'),
                  btnTitle: t('TRYAGAIN'),
                });
                return;
              }
              const content = isInsUserUrl(searchStr);
              rightStr.current = content;
              setTaskListData(null);
              if (content) {
                onSearch(content);
              } else {
                setOpenPrivateAccount({
                  title: t('Invalid Instagram Username'),
                  desc: t('The username you entered is not valid. Please check the username and try again.'),
                  btnTitle: t('TRYAGAIN'),
                });
                return;
              }
            }}
            onValueChanged={onValueChanged}
            inputPlaceholder={t('Enter an Instagram username or profile link')}
            middleContent={
              isLogin ? <SubContent currentUserPlan={currentUserPlan} t={t} exportNumber={exportNumber} /> : <></>
            }
            setType={setType}
            type={type}
          />
        </div>
        {/* CLS 优化：loading 时显示骨架屏，数据返回后显示真实内容 */}
        {loading && !currentInsUser && <InstagramUserInfoSkeleton />}
        {currentInsUser && (
          <>
            <InstagramUserInfo currentInsUser={currentInsUser} />
            {showPeal && (
              <div className="border max-w-[1000px] mt-10 shadow-lg rounded-lg mx-auto bg-white">
                {isSearchLoading ? (
                  <Searching
                    paused={openErrorAlterModalLimit !== null}
                    scrollToPosition={firstScrollPositionRef.current}
                    onComplete={() => {
                      // CLS 优化：检查是否偏离第一次滚动位置，偏离则滚动回去
                      const currentScrollTop = document.scrollingElement?.scrollTop || 0;
                      const targetScrollTop = firstScrollPositionRef.current ?? 0;
                      const scrollDiff = Math.abs(currentScrollTop - targetScrollTop);

                      if (scrollDiff > 10) {
                        // 用户已偏离第一次滚动位置，需要滚动回去再切换
                        document.scrollingElement?.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
                        let scrollTimer: NodeJS.Timeout;
                        const handleScrollEnd = () => {
                          clearTimeout(scrollTimer);
                          scrollTimer = setTimeout(() => {
                            window.removeEventListener('scroll', handleScrollEnd);
                            setIsSearchLoading(false);
                          }, 100);
                        };
                        window.addEventListener('scroll', handleScrollEnd);
                        handleScrollEnd();
                      } else {
                        // 用户仍在第一次滚动位置，直接切换
                        setIsSearchLoading(false);
                      }
                    }}
                  />
                ) : (
                  <DataPanel
                    type={type}
                    currentInsUser={currentInsUser}
                    taskListData={taskListData}
                    isUse={hasValidSubscription}
                    currentUserPlan={currentUserPlan}
                    refreshNumber={refreshNumber}
                    handleBack={() => {
                      setShowPeal(false);
                      setCurrentInsUser(null);
                      setTaskListData(null);
                      setSearchStr('');
                      numberRef.current = 0;
                      rightStr.current = '';
                    }}
                  />
                )}
              </div>
            )}
          </>
        )}

        <DownloadInstagramContent data={contentData?.contentInfo?.['section-1']} />
        <ChooseContent data={contentData?.contentInfo?.['section-2']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo?.['section-4']} />
        <CommentsPlan
          currentUserPlan={currentUserPlan}
          guideToRegisterOrLogin={guideToRegisterOrLogin}
          isLogin={isLogin}
        />
        <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </div>
      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        placeholder={t('Enter an Instagram username or profile link')}
        buttonTitle={t('Search Now')}
        loading={false}
        searchVal={searchStr}
        inputVisible={true}
        onValueChanged={(e) => {
          setSearchStr(e);
        }}
        onSearchClick={(e) => {
          // CLS 优化：滚动到标题对齐视口顶部（减去 header），并记录位置
          const targetScrollTop = getScrollToTitlePosition();
          firstScrollPositionRef.current = targetScrollTop;
          document.scrollingElement?.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
          if (loading || isSearchLoading) return;
          const searchStr = e.trim();
          const content = isInsUserUrl(searchStr);
          rightStr.current = content;
          setTaskListData(null);
          if (content) {
            onSearch(content);
          } else {
            setOpenPrivateAccount({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
            });
            return;
          }
        }}
      />

      {/*输入错误弹窗 */}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        btnDesc={openPrivateAccount?.btnDesc}
        closeAction={(isOk: boolean) => {
          setOpenPrivateAccount(null);
          setCurrentInsUser(null);
          if (isOk) {
          }
        }}
      />

      {/* 限制弹窗 */}
      <AlterModal
        visible={openErrorAlterModal !== null}
        title={openErrorAlterModal?.title}
        type={openErrorAlterModal?.type}
        btnTitle={openErrorAlterModal?.btnTitle}
        cancelBtnTitle={openErrorAlterModal?.cancelBtnTitle}
        desc={openErrorAlterModal?.desc}
        closeAction={() => {
          // CLS 优化：关闭弹窗时才关闭 loading，骨架屏和弹窗同时消失
          setOpenErrorAlterModal(null);
          setLoading(false);
        }}
        onCancel={() => {
          // CLS 优化：点击 No thanks，设置 currentInsUser 并开始任务
          setOpenErrorAlterModal(null);
          setLoading(false);
          setCurrentInsUser(currentUserRef.current);
          setShowPeal(true);
          setIsSearchLoading(true);
          createTask(500);
        }}
        onUpgrade={() => {
          // CLS 优化：点击升级时关闭 loading，骨架屏消失
          ga_logEvent(EventName.click_export_upgrade_plan);
          setOpenErrorAlterModal(null);
          setLoading(false);
          const PricingTitle = document.querySelector('#pricing-title');
          if (PricingTitle) {
            PricingTitle.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />
      {/* 查询点数不够 */}
      <AlterModalLimit
        visible={openErrorAlterModalLimit !== null}
        title={openErrorAlterModalLimit?.title}
        btnTitle={openErrorAlterModalLimit?.btnTitle}
        desc={openErrorAlterModalLimit?.desc}
        onCancel={() => {
          // CLS 优化：弹窗关闭时再重置状态，避免进度条突然消失
          setOpenErrorAlterModalLimit(null);
          setCurrentInsUser(null);
        }}
        onUpgrade={() => {
          ga_logEvent(EventName.click_export_upgrade_plan);
          // CLS 优化：弹窗关闭时再重置状态
          setOpenErrorAlterModalLimit(null);
          setCurrentInsUser(null);
          if (currentUserPlan?.name !== 'Business Monthly' && currentUserPlan?.name !== 'Business Yearly') {
            const PricingTitle = document.querySelector('#pricing-title');
            if (PricingTitle) {
              PricingTitle.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }}
      />

      <StartTastModal
        currentUserPlan={currentUserPlan}
        visible={openStartTastModal}
        type={type}
        onClose={() => {
          setOpenStartTastModal(false);
          setCurrentInsUser(null);
        }}
        onExport={(num) => {
          setOpenStartTastModal(false);
          createTask(num);
        }}
        onUpgrade={() => {
          ga_logEvent(EventName.click_export_upgrade_plan);
          setOpenStartTastModal(false);
          const PricingTitle = document.querySelector('#pricing-title');
          if (PricingTitle) {
            PricingTitle.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />
    </div>
  );
}

const SubContent = ({
  currentUserPlan,
  t,
  exportNumber,
}: {
  currentUserPlan: any;
  t: (s: string) => string;
  exportNumber: number;
}) => {
  const router = useRouter();
  const hasValidSubscription = useMemo(() => {
    return currentUserPlan?.expire_time ? new Date(currentUserPlan.expire_time) >= new Date() : false;
  }, [currentUserPlan?.expire_time]);
  return (
    <div className="flex gap-4 lg:w-[720px] w-full flex-wrap mx-auto  justify-center text-sm">
      <div className="flex gap-2">
        <div className="flex gap-2">
          <span className="font-bold">{t('Export Quota')}:</span>
          <span className="text-green-500">
            {hasValidSubscription ? t('Unlimited') : 3000 - exportNumber < 0 ? 0 : 3000 - exportNumber}
          </span>
        </div>
        <div className="flex gap-2">
          <span className="font-bold">{t('Current Plan')}:</span>
          <span className="text-green-500 font-bold">
            {hasValidSubscription && currentUserPlan?.name.includes('Business')
              ? t('Business')
              : hasValidSubscription && currentUserPlan?.name.includes('Pro')
                ? t('Pro')
                : t('Free')}
          </span>
          {(!currentUserPlan?.name.includes('Business') || !hasValidSubscription) && (
            <>
              <span
                className="text-[#725CFF] hover:underline font-bold cursor-pointer hidden lg:block"
                onClick={() => {
                  const PricingTitle = document.querySelector('#pricing-title');
                  if (PricingTitle) {
                    PricingTitle.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {t('Upgrade Now')}
              </span>
              <span
                className="text-[#725CFF] hover:underline font-bold cursor-pointer lg:hidden"
                onClick={() => {
                  const PricingTitle = document.querySelector('#pricing-title');
                  if (PricingTitle) {
                    PricingTitle.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {t('Upgrade')}
              </span>
            </>
          )}
        </div>
      </div>
      <div
        className="text-[#725CFF] hover:underline font-bold cursor-pointer"
        onClick={() => {
          router.push('/dashboard?dashboard_type=toolkit&featureType=follower_export');
        }}
      >
        {`${t('Export Dashboard')} >`}
      </div>
    </div>
  );
};
